import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import NewsletterSignup from "../NewsletterSignup/NewsletterSignup";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://api.axoix.in";

interface SidebarPost {
  slug: string;
  title: string;
  published_at: string | null;
  reading_time_mins: number | null;
}

const formatDate = (isoDate: string | null): string => {
  if (!isoDate) return "";
  const d = new Date(isoDate);
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const Icons = {
  search: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M14.002 5H20.002" stroke="#00020F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14.002 8H17.002" stroke="#00020F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M21.002 11.5C21.002 16.75 16.752 21 11.502 21C6.25195 21 2.00195 16.75 2.00195 11.5C2.00195 6.25 6.25195 2 11.502 2"
        stroke="#00020F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      />
      <path d="M22.002 22L20.002 20" stroke="#00020F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

const BlogSidebar: React.FC = () => {
  const [recentPosts, setRecentPosts] = useState<SidebarPost[]>([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");

  useEffect(() => {
    fetch(`${API_BASE}/api/chorus/blog/posts?site=axoix&status=published&limit=4`)
      .then((res) => res.json())
      .then((data) => {
        if (data.posts) setRecentPosts(data.posts);
      })
      .catch(() => {});
  }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    navigate(q ? `/blog?q=${encodeURIComponent(q)}` : "/blog");
  };

  return (
    <div className="col-lg-4 mt-50">
      <div className="sidebar">

        {/* SEARCH WIDGET */}
        <div className="sidebar_widget">
          <h3 className="sidebar_widget_title">Search</h3>
          <form className="form-group" onSubmit={submitSearch}>
            <input
              className="form-control"
              type="search"
              placeholder="Search articles..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search articles"
            />
            <button type="submit" className="search_icon" aria-label="Search">
              <span>{Icons.search}</span>
            </button>
          </form>
        </div>

        {/* NEWSLETTER SIGNUP */}
        <div className="sidebar_widget">
          <NewsletterSignup source="blog_sidebar" variant="inline" />
          <a href="/feed.xml" className="rss-subscribe-link" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 14, fontSize: 13, color: "var(--color-primary)" }}>
            <i className="fas fa-rss"></i> Subscribe via RSS
          </a>
        </div>

        {/* RECENT ARTICLES */}
        <div className="sidebar_widget">
          <h3 className="sidebar_widget_title">Recent Articles</h3>
          <ul className="recent_post_block list-unstyled">
            {recentPosts.map((post) => (
              <li key={post.slug} className="recent_post_item xb-border">
                <h3 className="post-title border-effect-2">
                  <Link to={`/blog/${post.slug}`}>
                    {post.title.length > 60 ? post.title.slice(0, 60) + "..." : post.title}
                  </Link>
                </h3>
                <span style={{ fontSize: 12, opacity: 0.5 }}>
                  {formatDate(post.published_at)} {post.reading_time_mins ? `· ${post.reading_time_mins} min read` : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* QUICK LINKS */}
        <div className="sidebar_widget">
          <h3 className="sidebar_widget_title">Explore AXOIX</h3>
          <ul className="category_list_block list-unstyled">
            <li>
              <Link to="/features">
                <span><i className="far fa-arrow-right"></i> All 22 Modules</span>
              </Link>
            </li>
            <li>
              <Link to="/industries">
                <span><i className="far fa-arrow-right"></i> 19 Industry Verticals</span>
              </Link>
            </li>
            <li>
              <Link to="/team">
                <span><i className="far fa-arrow-right"></i> 22 AI Characters</span>
              </Link>
            </li>
            <li>
              <Link to="/pricing">
                <span><i className="far fa-arrow-right"></i> Pricing & Plans</span>
              </Link>
            </li>
            <li>
              <Link to="/signup">
                <span><i className="far fa-arrow-right"></i> Start Free Trial</span>
              </Link>
            </li>
          </ul>
        </div>

        {/* TAGS */}
        <div className="sidebar_widget">
          <h3 className="sidebar_widget_title">Topics</h3>
          <ul className="tags_block list-unstyled">
            {["AI Automation", "UPI Payments", "Voice Commands", "CRM", "Accounting", "Phone System", "Industries", "Security"].map((tag, i) => (
              <li key={i}>
                <Link className="xb-border" to="/blog">{tag}</Link>
              </li>
            ))}
          </ul>
        </div>

      </div>
    </div>
  );
};

export default BlogSidebar;
